import type { INotificationEngagementEvent, INotificationStatusRow, NotificationChannel } from './types';

/**
 * The strongest thing we know the passenger did with the latest message: an offer
 * on their screen beats getting into the app, which beats opening our link, which
 * beats clicking it in the email, which beats reading the message itself.
 */
export interface IPassengerSeen {
  kind: 'viewed' | 'appOpened' | 'opened' | 'clicked' | 'readEmail' | 'readWhatsapp' | 'read';
  /** ISO-8601. */
  at: string;
}

type EngagementFacts = Partial<
  Pick<INotificationStatusRow, 'offerViewedAt' | 'appOpenedAt' | 'linkOpenedAt' | 'clickedAt' | 'readAt' | 'channel'>
>;

/** A read, said for the channel it came on. */
export function readKind(channel: NotificationChannel | null | undefined): IPassengerSeen['kind'] {
  return channel === 'EMAIL' ? 'readEmail' : channel === 'WHATSAPP' ? 'readWhatsapp' : 'read';
}

export function passengerSeen(row: EngagementFacts | null | undefined): IPassengerSeen | null {
  if (row?.offerViewedAt) return { kind: 'viewed', at: row.offerViewedAt };
  if (row?.appOpenedAt) return { kind: 'appOpened', at: row.appOpenedAt };
  if (row?.linkOpenedAt) return { kind: 'opened', at: row.linkOpenedAt };
  if (row?.clickedAt) return { kind: 'clicked', at: row.clickedAt };
  if (row?.readAt) return { kind: readKind(row.channel), at: row.readAt };
  return null;
}

/** Any of the three: the latest message demonstrably reached the passenger. */
export function passengerReached(row: EngagementFacts | null | undefined): boolean {
  return passengerSeen(row) !== null;
}

/** A repeat of the same thing within this window is the same moment (a reload, a tab switch). */
const ENGAGEMENT_REPEAT_MS = 15 * 60_000;

/**
 * The passenger's engagement as timeline moments, oldest first: a repeat of the same
 * kind within {@link ENGAGEMENT_REPEAT_MS} of the last one kept says nothing new.
 */
export function engagementMoments(events: readonly INotificationEngagementEvent[]): INotificationEngagementEvent[] {
  const lastKept = new Map<string, number>();
  return [...events]
    .filter((e) => Number.isFinite(Date.parse(e.at)))
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
    .filter((e) => {
      const at = Date.parse(e.at);
      const prev = lastKept.get(e.kind);
      if (prev !== undefined && at - prev < ENGAGEMENT_REPEAT_MS) return false;
      lastKept.set(e.kind, at);
      return true;
    });
}
